import { getApiErrorMessage } from "@core/utility/get-error-message";
import {
  createRewardController,
  CreateRewardModel,
  deleteRewardController,
  DeleteRewardModel,
  earnRewardController,
  EarnRewardModel,
  getCampaignRewardController,
  getMembersRewardController,
  GetResponseCampaignRewardModel,
  GetResponseRewardCampaignModel,
  GetResponseRewardMemberModel,
  GetResponseRewardModel,
  getRewardBalanceController,
  getRewardCampaignController,
  getRewardController,
  GetRewardPayload,
  getUserBalanceRewardController,
  manualUpgradeController,
  ManualUpgradeModel,
  MutateResponseRewardModel,
  QueryKeyReward,
  redeemRewardController,
  RedeemRewardModel,
  redeemV2RewardController,
  showConversionController,
  updateRewardController,
  UpdateRewardModel,
  UserBalanceResponse,
} from "@factory/model/query-model-reward";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { AxiosError } from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { authHeaders } from "../authentication/auth-header";
import { useSessionContext } from "../authentication/session.context";

export function useQyGetReward(
  search: string,
  limit = 99,
  offset = 0,
  order?: object,
  filter?: Record<string, string>,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetResponseRewardModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    search: string | undefined = undefined,
    limit: number | undefined = undefined,
    offset: number | undefined = undefined,
    order: object | undefined = undefined,
    filter: Record<string, string> | undefined = undefined,
    dateFilter: string | undefined = undefined,
    startDate: string | undefined = undefined,
    endDate: string | undefined = undefined
  ) => {
    await checkSession();

    showProgress();
    const operation = await getRewardController(
      search,
      limit,
      offset,
      order,
      { filter: JSON.stringify(filter) },
      dateFilter,
      startDate,
      endDate,
      authHeaders()
    );
    const response = (await operation()).data;
    return response["data"] as GetResponseRewardModel;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKeyReward, search, limit, offset, order, filter],
    queryFn: () => apiFn(search, limit, offset, order, filter),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetRewardById(
  id: string,
  enabled: boolean,
  onSuccess?:
    | ((data: GetResponseRewardModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (id: string, search = "", limit = 1, offset = 0) => {
    await checkSession();

    showProgress();
    const operation = await getRewardController(
      search,
      limit,
      offset,
      undefined,
      { filter: JSON.stringify({ code: id }) },
      undefined,
      undefined,
      undefined,
      authHeaders()
    );
    const response = (await operation()).data;
    return response["data"] as GetResponseRewardModel;
  };

  return useQuery({
    queryKey: [QueryKeyReward, id],
    enabled,
    queryFn: () => apiFn(id),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetCampaignReward(
  onSuccess?:
    | ((data: GetResponseCampaignRewardModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (payload: GetRewardPayload) => {
    await checkSession();

    showProgress();
    const operation = await getCampaignRewardController(
      payload.code,
      payload.tenant,
      authHeaders()
    );
    const response = (await operation()).data;
    return response as GetResponseCampaignRewardModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyCreateReward(
  onSuccess?:
    | ((data: MutateResponseRewardModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (payload: CreateRewardModel) => {
    await checkSession();

    showProgress();
    const operation = await createRewardController(payload, authHeaders());
    const response = (await operation()).data;
    return response as MutateResponseRewardModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyReward);
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyUpdateReward(
  onSuccess?:
    | ((data: MutateResponseRewardModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (payload: UpdateRewardModel) => {
    await checkSession();

    showProgress();
    const operation = await updateRewardController(payload, authHeaders());
    const response = (await operation()).data;
    return response["message"] as MutateResponseRewardModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyReward);
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyDeleteReward(
  onSuccess?:
    | ((data: MutateResponseRewardModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (payload: DeleteRewardModel) => {
    await checkSession();

    showProgress();
    const operation = await deleteRewardController(payload, authHeaders());
    const response = (await operation()).data;
    return response["message"] as MutateResponseRewardModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyReward);
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetRewardBalance(
  code: string,
  tenant: string,
  enabled: boolean,
  onSuccess?:
    | ((data: GetResponseRewardModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (code: string, tenant: string) => {
    await checkSession();

    showProgress();
    const operation = await getRewardBalanceController(
      code,
      tenant,
      authHeaders()
    );
    const response = (await operation()).data;
    return response["data"] as GetResponseRewardModel;
  };

  return useQuery({
    queryKey: [QueryKeyReward, code],
    queryFn: () => apiFn(code, tenant),
    enabled,
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyEarnReward(
  onSuccess?:
    | ((data: MutateResponseRewardModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (payload: EarnRewardModel) => {
    await checkSession();

    showProgress();
    const operation = await earnRewardController(payload, authHeaders());
    const response = (await operation()).data;
    return response as MutateResponseRewardModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyReward);
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetConversion(
  code: string,
  branch: string,
  tenant: string,
  enabled: boolean,
  onSuccess?:
    | ((data: GetResponseRewardModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (code: string, branch: string, tenant: string) => {
    await checkSession();

    showProgress();
    const operation = await showConversionController(
      code,
      branch,
      tenant,
      authHeaders()
    );
    const response = (await operation()).data;
    return response["data"] as GetResponseRewardModel;
  };

  return useQuery({
    queryKey: [QueryKeyReward, code],
    queryFn: () => apiFn(code, branch, tenant),
    enabled,
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyRedeemReward(
  onSuccess?:
    | ((data: MutateResponseRewardModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (payload: RedeemRewardModel) => {
    await checkSession();

    showProgress();
    const operation = await redeemRewardController(payload, authHeaders());
    const response = (await operation()).data;
    return response["data"] as MutateResponseRewardModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyReward);
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyRedeemRewardV2(
  onSuccess?:
    | ((data: MutateResponseRewardModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (payload: RedeemRewardModel) => {
    await checkSession();

    showProgress();
    const operation = await redeemV2RewardController(payload, authHeaders());
    const response = (await operation()).data;
    return response["data"] as MutateResponseRewardModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyReward);
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetMemberReward(
  search: string,
  limit = 99,
  offset = 0,
  order?: object,
  filter?: Record<string, string>,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetResponseRewardMemberModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    search: string | undefined = undefined,
    limit: number | undefined = undefined,
    offset: number | undefined = undefined,
    order: object | undefined = undefined,
    filter: Record<string, string> | undefined = undefined,
    dateFilter: string | undefined = undefined,
    startDate: string | undefined = undefined,
    endDate: string | undefined = undefined
  ) => {
    await checkSession();

    showProgress();
    const operation = await getMembersRewardController(
      search,
      limit,
      offset,
      order,
      {filter: JSON.stringify(filter)},
      dateFilter,
      startDate,
      endDate,
      authHeaders()
    );
    const response = (await operation()).data;
    return response["data"] as GetResponseRewardMemberModel;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKeyReward, search, limit, offset, order, filter],
    queryFn: () => apiFn(search, limit, offset, order, filter),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetUserBalance(
  code: string,
  tenant: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: UserBalanceResponse) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (code: string, tenant: string) => {
    await checkSession();

    showProgress();
    const operation = await getUserBalanceRewardController(
      code,
      tenant,
      authHeaders()
    );
    const response = (await operation()).data;
    return response["data"] as UserBalanceResponse;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKeyReward, code, tenant],
    queryFn: () => apiFn(code, tenant),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetRewardCampaign(
  cardNumber: string,
  tenant: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetResponseRewardCampaignModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (cardNumber: string, tenant: string) => {
    await checkSession();

    showProgress();
    const operation = await getRewardCampaignController(
      cardNumber,
      tenant,
      authHeaders()
    );
    const response = (await operation()).data;
    return response as GetResponseRewardCampaignModel;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKeyReward, cardNumber, tenant],
    queryFn: () => apiFn(cardNumber, tenant),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyManualUpgrade(
  onSuccess?:
    | ((data: MutateResponseRewardModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (payload: ManualUpgradeModel) => {
    await checkSession();

    showProgress();
    const operation = await manualUpgradeController(payload, authHeaders());
    const response = (await operation()).data;
    return response as MutateResponseRewardModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyReward);
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}
